import { RoundedFilledButton } from '@/components/buttons/Buttons';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdClose } from 'react-icons/md';
import AcknowledgeCheckboxGroup from '../components/acknowledge-checkbox/AcknowledgeCheckboxGroup';
import DeactivateReasonDropdown from '../components/deactivate/DeactivateReasonDropdown';
import '../components/deactivate/deactivate-modal.styles.css';
import { RemoveUserRequest } from '../RemoveUserRequest';
import { InputController } from '@/components/inputs/InputController';
import TextInput from '@/components/inputs/TextInput';

type DeactivateModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onDeactivate: (rq: RemoveUserRequest) => void;
};

const DeactivateModal = ({
	isOpen,
	onClose,
	onDeactivate,
}: DeactivateModalProps) => {
	const [ack1, setAck1] = useState(false);
	const [ack2, setAck2] = useState(false);
	const [ack3, setAck3] = useState(false);
	const [reason, setReason] = useState('');
	const [feedback, setFeedback] = useState('');

	const form = useForm<RemoveUserRequest>();

	const reasons = [
		{
			label: "There's suspicious or fraudulent activity on my account",
			value: 'suspicious activity',
		},
		{
			label: 'Difficulty resolving billing or technical issues',
			value: 'tech issues',
		},
		{
			label: "10% fee is too high / I'm moving to a different service",
			value: 'high fee',
		},
		{
			label: 'Duplicate account / I created the account incorrectly',
			value: 'account issue',
		},
		{ label: 'My company is closing', value: 'company closing' },
		{ label: 'I am no longer at the company', value: 'not in the company' },
		{ label: 'Other (Please tell us why)', value: 'other' },
	];

	const isDeactivationAllowed = ack1 && ack2 && ack3;

	if (!isOpen) return null;

	return (
		<div className="modal-overlay">
			<div className="modal-content">
				<div className="flex justify-between items-start">
					<h2 className="modal-title">We're sorry to see you go.</h2>
					<button
						type="button"
						className="text-onSurfaceVariant relative p-1 close-button"
						onClick={onClose}
					>
						<MdClose size={24} />
					</button>
				</div>

				<p className="modal-description">
					If you deactivate your account, you will lose access
					immediately
				</p>

				<p className="group-header">
					Check the following to acknowledge that:
				</p>
				<AcknowledgeCheckboxGroup
					ack1={ack1}
					setAck1={setAck1}
					ack2={ack2}
					setAck2={setAck2}
					ack3={ack3}
					setAck3={setAck3}
				/>

				<Controller
					name="Reason"
					control={form.control}
					render={({ field }) => (
						<DeactivateReasonDropdown
							reason={field.value ?? ""}
							setReason={field.onChange}
							reasons={reasons}
						/>
					)}
				/>
				<p className="group-header">
					Please tell us briefly what didn't work out for you
				</p>

				<InputController id="Feedback" control={form.control}>
					<TextInput label="Feedback" placeholder="Your feedback..." />
				</InputController>

				<div className="modal-buttons">
					<RoundedFilledButton
						id="cancel"
						label="Cancel"
						onClick={onClose}
						className="cancel-button"
					/>
					<RoundedFilledButton
						id="deactivate"
						label="Deactivate Account"
						onClick={form.handleSubmit(onDeactivate)}
						className="deactivate-account-button"
						disabled={!isDeactivationAllowed}
					/>
				</div>

				<hr className="modal-divider" />

				<p className="modal-footer">
					If you want to delete your account immediately,{' '}
					<span className="contact-us-footer">
						<a
							href="https://www.xpress.ph/contact-us"
							target="_blank"
							rel="noopener noreferrer"
							className="contact-us-footer"
						>
							contact us at support
						</a>
					</span>
					.
				</p>
			</div>
		</div>
	);
};

export default DeactivateModal;
